import {
	Dialog,
	AppBar,
	Toolbar,
	Typography,
	IconButton,
	DialogContent,
	DialogActions,
	Divider,
	Button,
	Grid,
	Alert,
	Card,
	CardMedia,
	CardContent,
} from '@mui/material'
import Transition from './Transition'
import CloseIcon from '@mui/icons-material/Close'
import SaveAsTwoToneIcon from '@mui/icons-material/SaveAsTwoTone'
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase'
import { BoardViewBg, BoardViewHeader } from './ApplicationStyles'
import moment from 'moment'
import { apiFileDownloadByFileName } from 'api/api'
import CheckIcon from '@mui/icons-material/Check'
import { BoardDetailType, FileItemType } from 'constants/types'
import DescriptionIcon from '@mui/icons-material/Description'

type BoardViewProps = {
	dialogOpen: boolean
	dialogClose: () => void
	dialogModify: () => void
	dataSet: BoardDetailType | null
}

const BoardView: React.FC<BoardViewProps> = ({
	dialogOpen,
	dialogClose,
	dialogModify,
	dataSet,
}) => {
	// 이미지 파일 새탭 보기
	const viewImage = (fileName: string, originalFileName: string) => {
		let imageWindow = window.open(
			'',
			'_blank',
			'width=800,height=600,scrollbars=yes'
		)
		void imageWindow?.document.write(
			`
			<html>
			<head>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1"
				/>
				<title>${originalFileName}</title>
			</head>
			<body>
				<img src="${process.env.REACT_APP_HOST}/common/file/view/${fileName}" alt="${originalFileName}" style="max-width: 100%; height: auto;" />
			</body>
			</html>
			`
		)
		imageWindow?.document.close()
	}

	return (
		<>
			<Dialog
				open={dialogOpen}
				onClose={dialogClose}
				slots={{
					transition: Transition,
				}}
				fullScreen
			>
				<AppBar sx={{ position: 'sticky' }}>
					<Toolbar>
						<Typography
							sx={{ flex: 1 }}
							variant="h6"
							component="div"
						>
							{dataSet?.title}
						</Typography>
						<IconButton
							edge="end"
							color="inherit"
							onClick={dialogClose}
							aria-label="close"
						>
							<CloseIcon />
						</IconButton>
					</Toolbar>
				</AppBar>
				<DialogContent>
					<BoardViewHeader
						container
						spacing={1}
					>
						<Grid
							size={{ xs: 2, md: 2 }}
							sx={{ display: 'flex' }}
						>
							<Alert
								severity={
									dataSet?.type === '[공지]'
										? 'warning'
										: dataSet?.type === '[중요]'
										? 'error'
										: 'success'
								}
								sx={{ width: '100%' }}
							>
								분류 : {dataSet?.type}
							</Alert>
						</Grid>
						<Grid
							size={{ xs: 10, md: 4 }}
							sx={{ display: 'flex' }}
						>
							<Alert
								severity="info"
								sx={{ width: '100%' }}
							>
								작성자 : {dataSet?.modId || dataSet?.regId}
							</Alert>
						</Grid>
						<Grid
							size={{ xs: 12, md: 6 }}
							sx={{ display: 'flex' }}
						>
							<Alert
								severity="info"
								sx={{ width: '100%' }}
							>
								{
									/* dataSet.updatedAt 값이 dataSet.createdAt 값보다 크면 '수정일'로 표시 */
									moment(dataSet?.updatedAt).isAfter(moment(dataSet?.createdAt))
										? '수정일'
										: '작성일'
								}
								{' : '}
								{moment(dataSet?.updatedAt).isAfter(moment(dataSet?.createdAt))
									? moment(dataSet?.updatedAt).format('YYYY-MM-DD HH:mm')
									: moment(dataSet?.createdAt).format('YYYY-MM-DD HH:mm')}
							</Alert>
						</Grid>
					</BoardViewHeader>
					<BoardViewBg
						className="view ql-editor"
						dangerouslySetInnerHTML={{ __html: dataSet?.content || '' }}
					/>
					{1 > 0 && (
						<>
							{dataSet?.files && dataSet.files.length > 0 && (
								<Alert
									icon={<CheckIcon fontSize="inherit" />}
									severity="success"
									sx={{ my: 2 }}
								>
									첨부파일
								</Alert>
							)}
							<div>
								<Grid
									container
									spacing={2}
									sx={{ mt: 1 }}
								>
									{dataSet?.files?.map((fileItem: FileItemType) => {
										return (
											<Grid
												size={{ xs: 6, md: 3, lg: 2 }}
												key={fileItem.id}
											>
												<Card sx={{ maxWidth: '100%' }}>
													{fileItem?.contentType?.startsWith('image') ? (
														<CardMedia
															sx={{ height: 150, width: '100%' }}
															image={`${process.env.REACT_APP_HOST}/common/file/view/${fileItem.storedFileName}`}
															title={fileItem.storedFileName}
															onClick={() =>
																viewImage(fileItem.storedFileName, fileItem.originalFileName)
															}
															style={{ cursor: 'pointer' }}
														/>
													) : (
														<div style={{ height: 150 }}>
															<DescriptionIcon />
														</div>
													)}
													<CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
														<Typography
															gutterBottom
															variant="body2"
															component="p"
															sx={{
																WebkitLineClamp: '1',
																textOverflow: 'ellipsis',
																display: '-webkit-box',
																WebkitBoxOrient: 'vertical',
																overflow: 'hidden',
															}}
														>
															{fileItem.originalFileName}
														</Typography>
														<Button
															type="button"
															variant="contained"
															color="info"
															size="small"
															onClick={() =>
																apiFileDownloadByFileName(
																	fileItem.storedFileName,
																	fileItem.originalFileName
																)
															}
														>
															다운로드
														</Button>
													</CardContent>
												</Card>
											</Grid>
										)
									})}
								</Grid>
							</div>
						</>
					)}
					<Divider sx={{ my: 2 }} />
					{dataSet?.comments && dataSet.comments.length > 0 ? (
						<>
							<Alert
								icon={<CheckIcon fontSize="inherit" />}
								severity="info"
								sx={{ mb: 2 }}
							>
								댓글
							</Alert>
							{dataSet.comments.map((comment, index) => (
								<Card
									key={index}
									sx={{ mb: 2 }}
								>
									<CardContent>
										<Typography
											variant="body2"
											color="textSecondary"
											sx={{ whiteSpace: 'pre-wrap' }}
										>
											{comment.content}
										</Typography>
										<Typography
											variant="caption"
											color="textSecondary"
											sx={{ display: 'block', mt: 1 }}
										>
											작성자: {comment.regId} | 작성일:{' '}
											{moment(comment.createdAt).format('YYYY-MM-DD HH:mm')}
										</Typography>
									</CardContent>
								</Card>
							))}
						</>
					) : null}
				</DialogContent>
				<Divider />
				<DialogActions>
					<Button
						variant="contained"
						onClick={dialogModify}
						color="primary"
						size="small"
						startIcon={<SaveAsTwoToneIcon />}
					>
						수정
					</Button>
					<Button
						variant="contained"
						onClick={dialogClose}
						color="secondary"
						size="small"
						startIcon={<PhonelinkEraseIcon />}
					>
						취소
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default BoardView
